import { useState, type ChangeEvent } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { SlupkiRankingowe, SzeregCzasowy } from '@/components/wykresy/Wykresy';
import { useAuth } from '@/lib/auth/AuthContext';
import {
  useDniDostaw, useFiltryDostaw, useKierowcyDostaw, useListaDostaw,
  usePodsumowanieDostaw, useSzkolyDostaw,
} from '@/lib/useAnalytics';
import type { ZapytanieRaportuDostaw } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * RAPORTY DOSTAW — CO SIĘ WYDARZYŁO NA DRODZE DO SZKOŁY
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ EKRAN MA ODPOWIADAĆ BEZ EKSPORTU DO ARKUSZA ───────────────────────────────────────────────┐
 * │ ile dowozów w tym tygodniu · ile na czas · ile spóźnionych · które szkoły miały problem     │
 * │ · którzy kierowcy się spóźniali · kto uprzedzał · czy uprzedzenia były trafne · jaka była   │
 * │ średnia i największa skala opóźnienia.                                                       │
 * │                                                                                              │
 * │ Dlatego domyślnym okresem jest BIEŻĄCY TYDZIEŃ, a nie „ostatnie 30 dni": pytanie brzmi      │
 * │ „jak nam idzie teraz", a nie „jak było średnio".                                             │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ DWIE MIARY, KTÓRYCH NIE WOLNO ZLAĆ W JEDNĄ „OCENĘ KIEROWCY" ───────────────────────────────┐
 * │ PUNKTUALNOŚĆ — czy dowozi na czas.                                                          │
 * │ TRAFNOŚĆ ZGŁOSZEŃ — czy prawidłowo uprzedza, że się spóźni.                                 │
 * │                                                                                              │
 * │ Kierowca z 50% punktualności i 100% trafności jest zupełnie inną sytuacją niż kierowca      │
 * │ z 50% punktualności i zerem zgłoszeń: pierwszy daje szkole czas na reakcję, drugi zostawia  │
 * │ ją z niespodzianką. Jedna wspólna liczba zgubiłaby dokładnie tę różnicę.                    │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * BEZ WYKRESÓW KOŁOWYCH — poziome słupki i tabele z dokładnymi wartościami. Wykres uzupełnia
 * liczby, nigdy ich nie zastępuje.
 */

const STATUSY_OPISY: Record<string, string> = {
  on_time: 'na czas',
  early: 'przed czasem',
  late_5: 'do 5 min',
  late_15: 'do 15 min',
  late_over_15: 'ponad 15 min',
};

/** Minuty jako liczba całkowita z jednostką. `null` to „nie policzono", nie zero. */
function minuty(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Math.round(v)} min`;
}

function procent(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${v}%`;
}

function dzienKrotko(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
}

/** Kafel pojedynczej liczby. Podpis mówi, czym ona JEST — nie tylko jak się nazywa. */
function Kafel({ etykieta, wartosc, podpis, uwaga }: {
  etykieta: string; wartosc: string; podpis?: string; uwaga?: boolean;
}) {
  return (
    <Box
      sx={{
        flex: '1 1 150px', minWidth: 140,
        p: 1.5, borderRadius: 'var(--r-maly, 8px)',
        border: '1px solid var(--linia)',
        background: uwaga ? 'var(--uwaga-tlo)' : 'var(--powierzchnia)',
      }}
    >
      <Typography sx={{ fontSize: 11, color: 'var(--tekst-slaby)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {etykieta}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2, color: uwaga ? 'var(--uwaga)' : 'inherit' }}>
        {wartosc}
      </Typography>
      {podpis && (
        <Typography sx={{ fontSize: 11, color: 'var(--tekst-slaby)', lineHeight: 1.3 }}>{podpis}</Typography>
      )}
    </Box>
  );
}

/** Presety okresu. Liczone od DZIŚ wstecz — poza „bieżącym tygodniem", który liczy serwer. */
function odDni(dni: number): { od: string; do: string } {
  const dzis = new Date();
  const doo = dzis.toISOString().slice(0, 10);
  const od = new Date(dzis.getTime() - (dni - 1) * 86400000).toISOString().slice(0, 10);
  return { od, do: doo };
}

export function RaportyDostawPage() {
  const auth = useAuth();
  const czyWlasciciel = auth.has('admin.users.read');

  const [restauracja, setRestauracja] = useState('');
  const [od, setOd] = useState('');
  const [doo, setDoo] = useState('');
  const [kierowca, setKierowca] = useState('');
  const [szkola, setSzkola] = useState('');
  const [punktualnosc, setPunktualnosc] = useState<'' | 'na_czas' | 'spoznione'>('');
  const [zgloszenie, setZgloszenie] = useState<'' | 'tak' | 'nie'>('');
  const [trafnosc, setTrafnosc] = useState<'' | 'trafne' | 'nietrafne' | 'nierozstrzygalne'>('');
  const [strona, setStrona] = useState(1);

  const wskazana = restauracja.trim() === '' ? undefined : Number(restauracja);
  const brakWskazania = czyWlasciciel && wskazana === undefined;

  const okres: ZapytanieRaportuDostaw = {
    restauracja: wskazana,
    od: od || undefined,
    do: doo || undefined,
  };
  const zFiltrami: ZapytanieRaportuDostaw = {
    ...okres,
    kierowca: kierowca ? Number(kierowca) : undefined,
    szkola: szkola ? Number(szkola) : undefined,
    punktualnosc: punktualnosc || undefined,
    zgloszenie: zgloszenie || undefined,
    trafnosc: trafnosc || undefined,
    strona,
  };

  const podsumowanie = usePodsumowanieDostaw(okres);
  const kierowcy = useKierowcyDostaw(okres);
  const szkoly = useSzkolyDostaw(okres);
  const dni = useDniDostaw(okres);
  const lista = useListaDostaw(zFiltrami);
  const filtry = useFiltryDostaw(okres);

  function ustawOkres(zakres: { od: string; do: string }) {
    setOd(zakres.od);
    setDoo(zakres.do);
    setStrona(1);
  }

  const dane = podsumowanie.data;
  const kpi = dane?.kpi;
  const zakres = dane?.zakres;

  /*
   * ┌─ ŻADNEGO FILTROWANIA PO STRONIE PRZEGLĄDARKI ──────────────────────────────┐
   * │ Pierwsza wersja zawężała „zgłoszone / bez zgłoszenia" TUTAJ, na wczytanej stronie.       │
   * │ Zawężenie po stronicowaniu nie jest filtrem — jest ukrywaniem wierszy: licznik mówił     │
   * │ „100", tabela pokazywała kilkanaście, a druga strona bywała pusta mimo że pasujących      │
   * │ dostaw było więcej.                                                                       │
   * │                                                                                            │
   * │ Wszystkie filtry idą teraz do zapytania. `wierszy_ogolem` opisuje ZBIÓR PO FILTRACH,      │
   * │ więc jest liczbą, którą wolno zacytować.                                                  │
   * └─────────────────────────────────────────────────────────────────────────────┘
   */
  const wiersze = lista.data?.dostawy ?? [];
  const stronOgolem = Math.max(
    1,
    Math.ceil((lista.data?.wierszy_ogolem ?? 0) / (lista.data?.rozmiar_strony ?? 50)),
  );

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Raporty dostaw"
        opis="Punktualność, kierowcy i zgłoszenia spóźnień — wyłącznie dla tej restauracji."
      />

      {/* ── OKRES ─────────────────────────────────────────────────────────────────────────── */}
      <Karta>
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {czyWlasciciel && (
              <TextField
                label="Restauracja (nr)" size="small" sx={{ width: 170 }} value={restauracja}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setRestauracja(e.target.value); setStrona(1);
                }}
                helperText="Konto właściciela nie ma własnej restauracji"
              />
            )}
            <Button size="small" onClick={() => { setOd(''); setDoo(''); setStrona(1); }}>
              Bieżący tydzień
            </Button>
            <Button size="small" onClick={() => ustawOkres(odDni(7))}>7 dni</Button>
            <Button size="small" onClick={() => ustawOkres(odDni(30))}>30 dni</Button>
            <Button size="small" onClick={() => ustawOkres(odDni(90))}>90 dni</Button>

            <TextField
              label="Od" type="date" size="small" value={od}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setOd(e.target.value); setStrona(1); }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Do" type="date" size="small" value={doo}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setDoo(e.target.value); setStrona(1); }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          {/* Okres, o który serwer FAKTYCZNIE zapytał — a nie ten, który wpisano w pola. */}
          {zakres && (
            <Typography sx={{ fontFamily: 'var(--krój-data)', fontSize: 13, color: 'var(--tekst-2)' }}>
              Okres: <strong>{zakres.od}</strong> → <strong>{zakres.do}</strong>
            </Typography>
          )}
        </Stack>
      </Karta>

      {brakWskazania && (
        <EmptyState
          title="Wskaż restaurację"
          description="Konto właściciela nie jest przypisane do restauracji, więc trzeba podać, czyje raporty oglądasz."
        />
      )}

      {!brakWskazania && podsumowanie.isLoading && <LoadingState />}
      {!brakWskazania && podsumowanie.error ? (
        <ErrorState error={podsumowanie.error} context="Raporty dostaw — podsumowanie okresu." />
      ) : null}

      {/*
        ŹRÓDŁO NIEPODŁĄCZONE TO NIE JEST OKRES BEZ SPÓŹNIEŃ.
        Trzy stany mają trzy różne komunikaty, bo prowadzą do trzech różnych działań.
      */}
      {!brakWskazania && dane && dane.zrodlo_podlaczone === false && (
        <Alert severity="warning">
          <AlertTitle>Funkcja oczekuje na integrację</AlertTitle>
          <Typography variant="body2">
            {dane.wyjasnienie
              ?? 'Nie ma czego zmierzyć — ta restauracja nie ma odpowiednika w systemie kierowców.'}
          </Typography>
          {dane.powod_braku && (
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>{dane.powod_braku}</Typography>
          )}
        </Alert>
      )}

      {/* ── KPI ───────────────────────────────────────────────────────────────────────────── */}
      {kpi && (
        <Karta>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
              <Kafel etykieta="Liczba dowozów" wartosc={String(kpi.dowozow)} />
              <Kafel etykieta="Na czas" wartosc={String(kpi.na_czas)} />
              <Kafel etykieta="Spóźnione" wartosc={String(kpi.spoznionych)} uwaga={kpi.spoznionych > 0} />
              <Kafel
                etykieta="% punktualności"
                wartosc={procent(kpi.punktualnosc_pct)}
                podpis={`z ${kpi.porownywalnych} porównywalnych`}
              />
              <Kafel
                etykieta="Średnie spóźnienie"
                wartosc={minuty(kpi.srednie_spoznienie_min)}
                podpis="tylko po spóźnionych"
              />
              <Kafel etykieta="Największe spóźnienie" wartosc={minuty(kpi.najwieksze_spoznienie_min)} />
            </Stack>

            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
              <Kafel etykieta="Zgłoszone spóźnienia" wartosc={String(kpi.zgloszen)} />
              <Kafel
                etykieta="Trafne zgłoszenia"
                wartosc={String(kpi.zgloszen_trafnych)}
                podpis={`z ${kpi.zgloszen_dopasowanych} dopasowanych`}
              />
              <Kafel etykieta="Nietrafne zgłoszenia" wartosc={String(kpi.zgloszen_nietrafnych)} />
              <Kafel etykieta="Trafność zgłoszeń" wartosc={procent(kpi.trafnosc_zgloszen_pct)} />
              <Kafel
                etykieta="Przed czasem"
                wartosc={String(kpi.przed_czasem + kpi.reczne_przed_przerwa)}
                podpis="poza mianownikiem punktualności"
              />
            </Stack>

            {/*
              ZASIĘG KPI POWIEDZIANY WPROST.
              Tabela niżej bywa zawężona filtrami, a te kafle — nigdy. Obie liczby są wtedy
              poprawne osobno i sprzeczne razem; bez tego zdania operator nie ma jak tego
              rozpoznać. Treść pochodzi z serwera, więc nie rozjedzie się z tym, co liczy.
            */}
            {dane?.zasieg_kpi && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">{dane.zasieg_kpi.opis}</Typography>
              </Alert>
            )}

            {/* Zastrzeżenia jadą z serwera i są pokazywane DOSŁOWNIE — liczba bez podstawy
                bywa czytana inaczej, niż została policzona. */}
            {dane?.zastrzezenia && dane.zastrzezenia.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {dane.zastrzezenia.map((z) => (
                  <Typography key={z} component="li" sx={{ fontSize: 12, color: 'var(--tekst-slaby)' }}>
                    {z}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        </Karta>
      )}

      {kpi && kpi.dowozow === 0 && (
        <EmptyState
          title="Brak dowozów w tym okresie"
          description="W wybranym zakresie nie zarejestrowano żadnej dostawy. To nie to samo, co dostawy bez spóźnień."
        />
      )}

      {/* ── PUNKTUALNOŚĆ WG KIEROWCY ──────────────────────────────────────────────────────── */}
      {(kierowcy.data?.kierowcy?.length ?? 0) > 0 && (
        <Karta>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800 }}>Kierowcy</Typography>
            <Typography variant="body2" sx={{ color: 'var(--tekst-2)' }}>
              Kierowca może wozić także dla innych restauracji. Widać tu wyłącznie jego dostawy
              wykonane <strong>dla Ciebie</strong>.
            </Typography>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, color: 'var(--tekst-slaby)', mb: 0.5 }}>
                  PUNKTUALNOŚĆ WG KIEROWCY
                </Typography>
                <SlupkiRankingowe
                  pozycje={(kierowcy.data?.kierowcy ?? [])
                    .filter((k) => k.punktualnosc_pct !== null)
                    .map((k) => ({ nazwa: k.kierowca, wartosc: k.punktualnosc_pct as number }))}
                  format={(v) => `${v}%`}
                  numeracja={false}
                  komunikatPusty="Za mało porównywalnych dostaw, żeby uszeregować kierowców."
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, color: 'var(--tekst-slaby)', mb: 0.5 }}>
                  TRAFNOŚĆ ZGŁOSZEŃ WG KIEROWCY
                </Typography>
                <SlupkiRankingowe
                  pozycje={(kierowcy.data?.kierowcy ?? [])
                    .filter((k) => k.trafnosc_zgloszen_pct !== null)
                    .map((k) => ({ nazwa: k.kierowca, wartosc: k.trafnosc_zgloszen_pct as number }))}
                  format={(v) => `${v}%`}
                  kolor="var(--seria-2)"
                  numeracja={false}
                  komunikatPusty="Nikt nie zgłosił spóźnienia, które dałoby się porównać z dostawą."
                />
              </Box>
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Kierowcy — punktualność i trafność zgłoszeń">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Kierowca</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Dowozów</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Na czas</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Spóźnionych</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Punktualność</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Śr. spóźn.</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Maks. spóźn.</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Zgłoszeń</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Trafnych</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Trafność</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(kierowcy.data?.kierowcy ?? []).map((k) => (
                    <TableRow key={k.kierowca_id ?? k.kierowca} hover>
                      <TableCell>{k.kierowca}</TableCell>
                      <TableCell align="right">{k.dowozow}</TableCell>
                      <TableCell align="right">{k.na_czas}</TableCell>
                      <TableCell align="right">{k.spoznionych}</TableCell>
                      <TableCell align="right">{procent(k.punktualnosc_pct)}</TableCell>
                      <TableCell align="right">{minuty(k.srednie_spoznienie_min)}</TableCell>
                      <TableCell align="right">{minuty(k.najwieksze_spoznienie_min)}</TableCell>
                      <TableCell align="right">{k.zgloszen}</TableCell>
                      <TableCell align="right">{k.zgloszen_trafnych}</TableCell>
                      <TableCell align="right">{procent(k.trafnosc_zgloszen_pct)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Karta>
      )}

      {/* ── SZKOŁY I DNI ──────────────────────────────────────────────────────────────────── */}
      {((szkoly.data?.szkoly?.length ?? 0) > 0 || (dni.data?.dni?.length ?? 0) > 0) && (
        <Karta>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ p: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, color: 'var(--tekst-slaby)', mb: 0.5 }}>
                SPÓŹNIENIA WG SZKOŁY
              </Typography>
              <SlupkiRankingowe
                pozycje={(szkoly.data?.szkoly ?? [])
                  .filter((s) => s.spoznionych > 0)
                  .map((s) => ({ nazwa: s.szkola, wartosc: s.spoznionych }))}
                format={(v) => String(v)}
                kolor="var(--uwaga)"
                komunikatPusty="Żadna szkoła nie miała spóźnionej dostawy w tym okresie."
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, color: 'var(--tekst-slaby)', mb: 0.5 }}>
                SPÓŹNIENIA WG DNIA
              </Typography>
              <SzeregCzasowy
                seria={(dni.data?.dni ?? []).map((d) => ({ etykieta: dzienKrotko(d.dzien), wartosc: d.spoznionych }))}
                format={(v) => String(v)}
                kolor="var(--uwaga)"
                etykietaSerii="Spóźnione dostawy"
                komunikatPusty="Za mało dni w okresie, żeby narysować przebieg."
              />
            </Box>
          </Stack>
        </Karta>
      )}

      {/* ── TABELA SZCZEGÓŁOWA ────────────────────────────────────────────────────────────── */}
      {!brakWskazania && dane?.zrodlo_podlaczone !== false && (
        <Karta>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800 }}>Dostawy</Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <TextField
                select size="small" label="Kierowca" sx={{ minWidth: 170 }} value={kierowca}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setKierowca(e.target.value); setStrona(1); }}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                <MenuItem value="">wszyscy</MenuItem>
                {(filtry.data?.kierowcy ?? []).map((k) => (
                  <MenuItem key={k.id} value={String(k.id)}>{k.nazwa}</MenuItem>
                ))}
              </TextField>

              <TextField
                select size="small" label="Szkoła" sx={{ minWidth: 200 }} value={szkola}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setSzkola(e.target.value); setStrona(1); }}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                <MenuItem value="">wszystkie</MenuItem>
                {(filtry.data?.szkoly ?? []).map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.nazwa}</MenuItem>
                ))}
              </TextField>

              <ToggleButtonGroup
                size="small" exclusive value={punktualnosc}
                onChange={(_e, v) => { setPunktualnosc((v ?? '') as '' | 'na_czas' | 'spoznione'); setStrona(1); }}
                aria-label="Punktualność"
              >
                <ToggleButton value="">Wszystkie</ToggleButton>
                <ToggleButton value="na_czas">Na czas</ToggleButton>
                <ToggleButton value="spoznione">Spóźnione</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                size="small" exclusive value={zgloszenie}
                onChange={(_e, v) => { setZgloszenie((v ?? '') as '' | 'tak' | 'nie'); setStrona(1); }}
                aria-label="Zgłoszenie spóźnienia"
              >
                <ToggleButton value="">Zgłoszenie: obojętne</ToggleButton>
                <ToggleButton value="tak">Zgłoszone</ToggleButton>
                <ToggleButton value="nie">Bez zgłoszenia</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                select size="small" label="Trafność zgłoszenia" sx={{ minWidth: 190 }} value={trafnosc}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setTrafnosc(e.target.value as '' | 'trafne' | 'nietrafne' | 'nierozstrzygalne');
                  setStrona(1);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                <MenuItem value="">obojętna</MenuItem>
                <MenuItem value="trafne">trafne</MenuItem>
                <MenuItem value="nietrafne">nietrafne</MenuItem>
                {/* Osobna pozycja, nie worek na resztę: zgłoszenie o nieznanym poziomie
                    nie jest błędem kierowcy i nie może być liczone jako nietrafne. */}
                <MenuItem value="nierozstrzygalne">nierozstrzygalne</MenuItem>
              </TextField>
            </Stack>

            {/* Liczba opisuje ZBIÓR PO FILTRACH — to ją operator cytuje, więc ma być na widoku. */}
            {lista.data?.wierszy_ogolem !== undefined && (
              <Typography sx={{ fontSize: 13, color: 'var(--tekst-2)' }}>
                Pasujących dostaw: <strong>{lista.data.wierszy_ogolem}</strong>
                {stronOgolem > 1 && <> · strona {strona} z {stronOgolem}</>}
              </Typography>
            )}

            {lista.isLoading && <LoadingState />}
            {lista.error ? <ErrorState error={lista.error} context="Raporty dostaw — lista dostaw." /> : null}

            {!lista.isLoading && !lista.error && wiersze.length === 0 && (
              <EmptyState
                title="Brak dostaw dla tych filtrów"
                description="Zmień zakres dat albo poluzuj filtry. Pusty wynik nie oznacza, że dostaw nie było."
              />
            )}

            {wiersze.length > 0 && (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ overflowX: 'auto', opacity: lista.isFetching ? 0.6 : 1, transition: 'opacity 120ms' }}
              >
                <Table size="small" aria-label="Szczegóły dostaw">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Kierowca</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Plan</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Dostarczono</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Odchylenie</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Zgłoszenie</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Trafność</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wiersze.map((w) => (
                      <TableRow key={w.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'var(--krój-data)' }}>{w.dzien}</TableCell>
                        <TableCell>{w.szkola}</TableCell>
                        <TableCell>{w.kierowca}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'var(--krój-data)' }}>
                          {w.planowana ?? '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'var(--krój-data)' }}>
                          {w.dostarczono ? w.dostarczono.slice(11, 16) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {w.odchylenie_min === null ? '—' : `${w.odchylenie_min > 0 ? '+' : ''}${w.odchylenie_min} min`}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={STATUSY_OPISY[w.status] ?? w.status}
                            color={w.status.startsWith('late') ? 'warning' : 'default'}
                            variant={w.status.startsWith('late') ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {w.zgloszenie
                            ? `${w.zgloszenie.zgloszony_poziom}${w.zgloszenie.zgloszono ? ` · ${w.zgloszenie.zgloszono.slice(11, 16)}` : ''}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {/* `null` = nie da się rozstrzygnąć. Nie wolno tego pokazać jako „nietrafne". */}
                          {w.zgloszenie === null
                            ? '—'
                            : w.zgloszenie.trafne === null
                              ? <Chip size="small" variant="outlined" label="nierozstrzygalne" />
                              : (
                                <Chip
                                  size="small"
                                  color={w.zgloszenie.trafne ? 'success' : 'warning'}
                                  label={w.zgloszenie.trafne ? 'trafne' : 'nietrafne'}
                                />
                              )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {stronOgolem > 1 && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button size="small" disabled={strona <= 1} onClick={() => setStrona((s) => s - 1)}>
                  Poprzednia
                </Button>
                <Typography variant="body2">{strona} / {stronOgolem}</Typography>
                <Button size="small" disabled={strona >= stronOgolem} onClick={() => setStrona((s) => s + 1)}>
                  Następna
                </Button>
              </Stack>
            )}
          </Stack>
        </Karta>
      )}
    </Stack>
  );
}

export default RaportyDostawPage;
